import * as React from "react";
import { CheckIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getFullName, sortByStringKey } from "@revelate/utils";
import { NestedPlan, UserWithDeals } from "@revelate/types";
import { useAppStore } from "@/stores";
import type { KeyedMutator } from "swr";
import { TableType, addUserToPlan, getAll } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import useSWR from "swr";
import { Loader } from "@/components/Loader";

export function EmployeeCombobox({
  plan,
  mutate,
}: {
  plan: NestedPlan;
  mutate: KeyedMutator<NestedPlan | null>;
}) {
  const [open, setOpen] = React.useState(false);
  const currentUser = useAppStore((state) => state.currentUser);

  const {
    data: employees,
    error,
    isLoading,
  } = useSWR(
    [
      "users" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]): Promise<UserWithDeals[]> => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const handleSelect = async (fullName: string) => {
    setOpen(false);
    const user = employees?.find((e) => getFullName(e) === fullName);
    if (!user) return;
    await addUserToPlan(plan.id, user.id);
    const updatedUsers = [...(plan.users || []), user];
    mutate({
      ...plan,
      users: updatedUsers,
    } as NestedPlan);
  };

  if (isLoading) return <Loader />;
  if (error) return <div>Error loading employees</div>;

  return (
    <div className="flex items-center space-x-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button>
            Add new employee
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" side="right" align="start">
          <Command>
            <CommandInput placeholder="Search employee..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {employees
                  ?.filter(
                    (e) =>
                      !plan?.users.map((u) => u.id).includes(e.id) &&
                      !plan?.teams
                        .flatMap((t) => t.users.map((u) => u.id))
                        .includes(e.id)
                  )
                  ?.sort(sortByStringKey("first_name"))
                  .map((user) => (
                    <CommandItem
                      key={user.id}
                      value={getFullName(user)}
                      onSelect={(id) => handleSelect(id)}
                    >
                      <span>{getFullName(user)}</span>
                      <CheckIcon
                        className={cn("ml-auto h-4 w-4", "opacity-0")}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
